import { getFirebaseServices } from '@/config/firebase'; 
import { logEvent as firebaseLogEvent, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { useConsentStore } from '@/stores/consentStore'; 

interface FirebaseEventParams { [key: string]: any; }
export const logFirebaseEvent = async (eventName: string, params?: FirebaseEventParams): Promise<void> => {
  const { consent } = useConsentStore.getState();
  const { analytics } = await getFirebaseServices();
  if (analytics && consent.analytics === 'granted') { 
    try { firebaseLogEvent(analytics, eventName, params); }
    catch (error) { console.error(`Marketing: Error logging Firebase Analytics event: ${eventName}`, error); }
  }
};

declare global { 
  interface Window {
    fbq?: (...args: any[]) => void; 
    rdt?: { (action: string, eventNameOrPixelId: string, eventDataOrOptions?: object): void; sendEvent?: (...args: any[]) => void; callQueue?: any[]; };
    dataLayer?: any[]; gtag?: (...args: any[]) => void;
    ttq?: { (action: string, eventNameOrPixelId: string, eventDataOrOptions?: object): void; page: (...args: any[]) => void; track: (...args: any[]) => void; load: (pixelId: string) => void; instance: (pixelId: string) => any; _i?: any; methods?: string[]; setAndDefer?: (...args: any[]) => void; };
    $crisp?: any[];
    CRISP_WEBSITE_ID?: string;
  }
}

let isFbPixelInitialized = false;
let isRedditPixelInitialized = false;
let isGoogleTagInitialized = false;
let isTikTokPixelInitialized = false;
let isCrispChatInitialized = false;
let trackersInitPromise: Promise<void> | null = null;

const appendScriptOnce = (scriptId: string, src: string) => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(scriptId)) return;
  const s = document.createElement('script');
  s.id = scriptId;
  s.async = true;
  s.src = src;
  document.head.appendChild(s);
};

const ensureFacebookPixelBase = () => {
  if (typeof window === 'undefined') return;
  if (!window.fbq) {
    const fbq = function (...args: any[]) {
      ((fbq as any).queue = (fbq as any).queue || []).push(args);
    } as any;
    (fbq as any).queue = [];
    (fbq as any).loaded = true;
    (fbq as any).version = '2.0';
    window.fbq = fbq;
    (window as any)._fbq = fbq;
  }
  appendScriptOnce('fb-pixel-base', 'https://connect.facebook.net/en_US/fbevents.js');
};

const ensureRedditPixelBase = () => {
  if (typeof window === 'undefined') return;
  if (!window.rdt) {
    const rdt = function (...args: any[]) {
      ((rdt as any).callQueue = (rdt as any).callQueue || []).push(args);
    } as any;
    (rdt as any).callQueue = [];
    window.rdt = rdt;
  }
  appendScriptOnce('reddit-pixel-base', 'https://www.redditstatic.com/ads/pixel.js');
};

const ensureTikTokPixelBase = () => {
  if (typeof window === 'undefined') return;
  if (!window.ttq) {
    const ttq: any = function (...args: any[]) {
      (ttq.q = ttq.q || []).push(args);
    };
    ttq.q = [];
    ttq.load = (id: string) => ttq('load', id);
    ttq.page = (...args: any[]) => ttq('page', ...args);
    ttq.track = (...args: any[]) => ttq('track', ...args);
    ttq.instance = () => ttq;
    window.ttq = ttq;
  }
  appendScriptOnce('tiktok-pixel-base', 'https://analytics.tiktok.com/i18n/pixel/events.js');
};

export const initFacebookPixel = () => {
  const { consent } = useConsentStore.getState();
  if (isFbPixelInitialized || typeof window === 'undefined' || consent.advertising !== 'granted') {
    if (consent.advertising !== 'granted' && typeof window !== 'undefined' && window.fbq) console.log('Marketing: Facebook Pixel not initialized (consent advertising:', consent.advertising,')');
    return;
  }
  const pixelId = import.meta.env.VITE_FACEBOOK_PIXEL_ID;
  if (pixelId) {
    ensureFacebookPixelBase();
    try {
      if (typeof window.fbq !== 'function') return;
      window.fbq('init', pixelId);
      window.fbq('track', 'PageView');
      isFbPixelInitialized = true;
      console.log('Marketing: Facebook Pixel Initialized. ID:', pixelId);
    }
    catch (error) { console.error('Marketing: Error initializing Facebook Pixel:', error); }
  } else { console.log('Marketing: Facebook Pixel ID (VITE_FACEBOOK_PIXEL_ID) not found.'); }
};

export const trackFacebookEvent = (eventName: string, params?: object) => {
  const { consent } = useConsentStore.getState();
  if (!isFbPixelInitialized || typeof window === 'undefined' || typeof window.fbq !== 'function' || consent.advertising !== 'granted') {
    return;
  }
  try { window.fbq('track', eventName, params); }
  catch (error) { console.error(`Marketing: Error tracking FB Pixel event ${eventName}:`, error); }
};

export const initRedditPixel = () => {
  const { consent } = useConsentStore.getState();
  if (isRedditPixelInitialized || typeof window === 'undefined' || consent.advertising !== 'granted') {
     if (consent.advertising !== 'granted' && typeof window !== 'undefined' && window.rdt) console.log('Marketing: Reddit Pixel not initialized (consent advertising:', consent.advertising,')');
    return;
  }
  const pixelId = import.meta.env.VITE_REDDIT_PIXEL_ID;
  if (pixelId) { ensureRedditPixelBase(); try {
    if (typeof window.rdt !== 'function') return;
    window.rdt('init', pixelId, { "optOut": false, "useDecimalCurrencyValues": true });
    window.rdt('track', 'PageVisit');
    isRedditPixelInitialized = true;
    console.log('Marketing: Reddit Pixel Initialized.');
  } catch (e) { console.error('Marketing: Error init Reddit Pixel', e);}}
};
export const trackRedditEvent = (eventName: string, eventData?: object) => {
  const { consent } = useConsentStore.getState();
   if (!isRedditPixelInitialized || typeof window === 'undefined' || typeof window.rdt !== 'function' || consent.advertising !== 'granted') {
    return;
  }
  try { window.rdt('track', eventName, eventData); }
  catch (error) { console.error(`Marketing: Error tracking Reddit Pixel event ${eventName}:`, error); }
};

// --- Google Ads (gtag.js) ---
export const initGoogleAds = () => {};
export const trackGoogleAdsConversion = (conversionLabel: string, params?: object) => {
  const { consent } = useConsentStore.getState();
  if (typeof window === 'undefined' || typeof window.gtag !== 'function' || consent.advertising !== 'granted') {
    return;
  }
  const id = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID;
  if (id) { try { window.gtag('event', 'conversion', {'send_to': `${id}/${conversionLabel}`, ...params }); } catch(e){console.error('Marketing: Error track Google Ads',e);}}
};

export const initTikTokPixel = () => {
  const { consent } = useConsentStore.getState();
  if (isTikTokPixelInitialized || typeof window === 'undefined' || consent.advertising !== 'granted') {
    if (consent.advertising !== 'granted' && typeof window !== 'undefined' && window.ttq) console.log('Marketing: TikTok Pixel not initialized (consent advertising:', consent.advertising,')');
    return;
  }
  const id = import.meta.env.VITE_TIKTOK_PIXEL_ID;
  if (id) { ensureTikTokPixelBase(); try {
    if (!window.ttq || typeof window.ttq.load !== 'function' || typeof window.ttq.page !== 'function') return;
    window.ttq.load(id);
    window.ttq.page();
    isTikTokPixelInitialized = true;
    console.log('Marketing: TikTok Pixel Initialized.');
  } catch(e){console.error('Marketing: Error init TikTok',e);}}
};
export const trackTikTokEvent = (eventName: string, params?: object) => {
  const { consent } = useConsentStore.getState();
  if (!isTikTokPixelInitialized || typeof window === 'undefined' || !window.ttq || typeof window.ttq.track !== 'function' || consent.advertising !== 'granted') {
    return;
  }
  try { window.ttq.track(eventName, params); } catch(e){console.error('Marketing: Error track TikTok',e);}
};

// --- Crisp Chat (Functional) ---
export const initCrispChat = () => {
  const { consent } = useConsentStore.getState();
  const crispWebsiteId = import.meta.env.VITE_CRISP_WEBSITE_ID;

  if (isCrispChatInitialized || typeof window === "undefined" || !crispWebsiteId) {
    if (!crispWebsiteId) console.log("Marketing: Crisp Chat ID (VITE_CRISP_WEBSITE_ID) not found.");
    return;
  }

  if (consent.functional === "granted") {
    try {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = crispWebsiteId;
      const d = document;
      const s = d.createElement("script");
      s.src = "https://client.crisp.chat/l.js";
      s.async = true;
      d.getElementsByTagName("head")[0].appendChild(s);
      isCrispChatInitialized = true;
      console.log("Marketing: Crisp Chat Initialized.");
    } catch (error) {
      console.error("Marketing: Error initializing Crisp Chat:", error);
    }
  } else {
    console.log("Marketing: Crisp Chat not initialized (consent functional:", consent.functional, ")");
  }
};

export const initializeAllTrackersBasedOnConsent = async () => {
  if (trackersInitPromise) return trackersInitPromise;
  trackersInitPromise = (async () => {
  console.log("Marketing: Attempting to initialize all trackers based on current consent...");
    const { analytics } = await getFirebaseServices();
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, useConsentStore.getState().consent.analytics === 'granted');
    }
    initFacebookPixel();
    initRedditPixel();
    initTikTokPixel();
    initCrispChat();
  })();
  try {
    await trackersInitPromise;
  } finally {
    trackersInitPromise = null;
  }
};

useConsentStore.subscribe(
  (state, prevState) => {
    const functionalConsentChanged = state.consent.functional === 'granted' && prevState.consent.functional !== 'granted';
    const advertisingConsentChanged = state.consent.advertising === 'granted' && prevState.consent.advertising !== 'granted';
    const analyticsConsentChanged = state.consent.analytics !== prevState.consent.analytics;

    if (state.hasMadeChoice && state.hasMadeChoice !== prevState.hasMadeChoice) {
        console.log("Marketing: Consent choice made/updated, re-evaluating tracker initialization.");
        initializeAllTrackersBasedOnConsent();
    } else if (functionalConsentChanged || advertisingConsentChanged || analyticsConsentChanged) {
        console.log("Marketing: Specific consent category granted, re-evaluating tracker initialization.");
        initializeAllTrackersBasedOnConsent();
    }
  }
);
